import { ButtonHTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { connectedParticipantsObserver } from '@livekit/components-core'
import { RemoteParticipant, RoomEvent } from 'livekit-client'
import { ParticipantPermission } from 'livekit-server-sdk'

import { useMaybeRoomContext, useLayoutContext } from '@/src/context'

import { mergeProps } from '@/src/mergeProps'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { setCurrentTab } from '@/src/store/slices/pip'
import { selectIsAdmin } from '@/src/store/slices/meetData'
import { selectV } from '@/src/store/slices/room'
import { setIsOpenMedia } from '@/src/store/slices/media'
import { setIsOpenChat } from '@/src/store/slices/chat'

function setupParticipantsToggle() {
  const className: string = [prefixClass('button'), prefixClass('participants-toggle')].join(' ')
  return { className }
}

function prefixClass(unprefixedClassName) {
  return `lk-${unprefixedClassName}` as const
}

/** @public */
export interface UseChatToggleProps {
  props: ButtonHTMLAttributes<HTMLButtonElement>
}

/**
 * The `useChatToggle` hook provides state and functions for toggling the chat window.
 * @remarks
 * Depends on the `LayoutContext` to work properly.
 * @see {@link ChatToggle}, {@link Chat}
 * @public
 */
export function useParticipantsToggle({ props }: UseChatToggleProps) {
  const roomContext = useMaybeRoomContext()
  const { dispatch, state } = useLayoutContext().widget

  const isAdmin = useAppSelector(selectIsAdmin)
  const isTextAssistant = useAppSelector(selectV) === 'hr'
  const isCurator = roomContext?.localParticipant?.attributes?.isCurator === 'true'
  const showButton = isAdmin || isCurator
  const dispatchPiP = useAppDispatch()

  const [waitParticipant, setWaitParticipant] = useState(0)
  const initialRoomContext = useRef(roomContext)
  const initialShowParticipants = useRef(state.showParticipants)

  useEffect(() => {
    dispatch?.({ msg: 'waiting_participants', count: waitParticipant })
  }, [waitParticipant, dispatch])

  useEffect(() => {
    if (isTextAssistant) return

    const checkRemoteParticipants = () => {
      initialRoomContext.current?.remoteParticipants.forEach((participant) => {
        if (
          !participant.permissions?.hidden &&
          !participant.permissions?.agent &&
          !participant.permissions?.canSubscribe &&
          !participant.permissions?.canPublish &&
          !participant.permissions?.recorder &&
          !initialShowParticipants.current
        ) {
          setWaitParticipant((prev) => prev + 1)
        }
      })
    }

    setWaitParticipant(0)
    checkRemoteParticipants()
  }, [isTextAssistant])

  useEffect(() => {
    if (!roomContext) return

    if (isTextAssistant) {
      const subscription = connectedParticipantsObserver(roomContext, {
        additionalRoomEvents: [RoomEvent.ParticipantPermissionsChanged],
      }).subscribe((participants) => {
        if (state.showParticipants) {
          setWaitParticipant(0)
          return
        }

        setWaitParticipant(
          participants.filter(
            (participant) =>
              participant.name !== 'Recorder' &&
              !participant.permissions?.hidden &&
              !participant.permissions?.agent &&
              !participant.permissions?.recorder &&
              !participant.permissions?.canSubscribe &&
              !participant.permissions?.canPublish,
          ).length,
        )
      })

      return () => subscription.unsubscribe()
    }

    let waitingParticipantsArr: RemoteParticipant[] = []

    const handleParticipantConnected = (participant: RemoteParticipant) => {
      if (waitingParticipantsArr.find((elem) => elem.sid === participant.sid)) return
      if (participant.name === 'Recorder') return

      if (
        !participant.permissions?.hidden &&
        !participant.permissions?.agent &&
        !participant.permissions?.canSubscribe &&
        !participant.permissions?.canPublish &&
        !participant.permissions?.recorder &&
        !state.showParticipants
      ) {
        setWaitParticipant((prev) => prev + 1)
        waitingParticipantsArr.push(participant)
      }
    }

    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      if (participant.name === 'Recorder') return

      if (
        !participant.permissions?.hidden &&
        !participant.permissions?.agent &&
        !participant.permissions?.canSubscribe &&
        !participant.permissions?.canPublish &&
        !participant.permissions?.recorder &&
        !state.showParticipants
      ) {
        setWaitParticipant((prev) => (prev > 0 ? prev - 1 : 0))
        waitingParticipantsArr.filter((item) => item.sid === participant.sid)
      }
    }

    const handleParticipantPermissionChanged = (
      prevPermission: ParticipantPermission,
      participant: RemoteParticipant,
    ) => {
      if (participant.name === 'Recorder') return

      if (
        !participant.permissions?.hidden &&
        !participant.permissions?.agent &&
        !participant.permissions?.recorder &&
        participant.permissions?.canSubscribe &&
        participant.permissions?.canPublish &&
        !prevPermission?.canPublish &&
        !prevPermission?.canSubscribe &&
        !state.showParticipants
      ) {
        setWaitParticipant((prev) => (prev > 0 ? prev - 1 : 0))
        waitingParticipantsArr.filter((item) => item.sid === participant.sid)
      }
    }

    roomContext
      .on(RoomEvent.ParticipantConnected, handleParticipantConnected)
      .on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
      .on(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionChanged)

    if (state.showParticipants) {
      setWaitParticipant(0)
      waitingParticipantsArr = []
    }

    return () => {
      roomContext
        .off(RoomEvent.ParticipantConnected, handleParticipantConnected)
        .off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
        .off(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionChanged)
    }
  }, [
    isTextAssistant,
    roomContext,
    state.showParticipants,
    roomContext?.remoteParticipants.size,
  ])

  const waitParticipants = isTextAssistant ? waitParticipant : state.waitingParticipants

  const mergedProps = useMemo(
    () =>
      mergeProps(props, {
        className: setupParticipantsToggle().className,
        onClick: () => {
          if (dispatch) dispatch({ msg: 'toggle_participants' })
          if (state?.showParticipants) {
            dispatchPiP(setCurrentTab('home'))
          } else {
            dispatchPiP(setCurrentTab('participants'))
            // Close media panel and chat when opening participants
            dispatchPiP(setIsOpenMedia(false))
            dispatchPiP(setIsOpenChat(false))
          }
        },
        'aria-pressed': state?.showParticipants ? 'true' : 'false',
        'data-lk-unread-msgs':
          waitParticipants < 10 ? waitParticipants.toFixed(0) : '9+',
      }),
    [props, dispatch, state?.showParticipants, waitParticipants, dispatchPiP],
  )

  return { mergedProps, waitParticipants, showButton }
}
